import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { cn } from "@/lib/utils";
import { SnapshotCompare } from "./SnapshotCompare";
import { 
  Shield,
  Globe,
  Plus,
  RefreshCw,
  Trash2,
  ExternalLink,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Search,
  FileText,
  Lock,
  Link2,
  Zap,
  Image as ImageIcon,
  Smartphone,
  Eye,
  Info,
  XCircle,
  Download,
  Folder
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Category configuration
const CATEGORIES = {
  indexability: { label: "Indexability", icon: Eye, color: "text-purple-500", bg: "bg-purple-500" },
  content: { label: "Content", icon: FileText, color: "text-blue-500", bg: "bg-blue-500" },
  links: { label: "Links", icon: Link2, color: "text-cyan-500", bg: "bg-cyan-500" },
  images: { label: "Images", icon: ImageIcon, color: "text-pink-500", bg: "bg-pink-500" },
  technical: { label: "Technical", icon: Shield, color: "text-orange-500", bg: "bg-orange-500" },
  mobile: { label: "Mobile", icon: Smartphone, color: "text-green-500", bg: "bg-green-500" },
  security: { label: "Security", icon: Lock, color: "text-red-500", bg: "bg-red-500" },
  performance: { label: "Performance", icon: Zap, color: "text-yellow-500", bg: "bg-yellow-500" },
};

// Health Score Meter Component
const HealthMeter = ({ score, size = 100 }) => {
  const strokeWidth = 8;
  const radius = (size - strokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  
  const getColor = (score) => {
    if (score >= 80) return { stroke: "#22c55e", text: "text-green-500", label: "Healthy" };
    if (score >= 60) return { stroke: "#eab308", text: "text-yellow-500", label: "Fair" };
    if (score >= 40) return { stroke: "#f97316", text: "text-orange-500", label: "Needs Work" };
    return { stroke: "#ef4444", text: "text-red-500", label: "Critical" };
  };
  
  const colors = getColor(score);
  
  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#27272a"
            strokeWidth={strokeWidth}
            fill="transparent"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={colors.stroke}
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-700"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={cn("text-3xl font-bold font-mono", colors.text)}>
            {score}
          </span>
          <span className="text-[10px] text-zinc-500 font-mono">/100</span>
        </div>
      </div>
      <span className={cn("text-xs font-mono mt-1", colors.text)}>{colors.label}</span>
    </div>
  );
};

// Category Score Bar
const CategoryScoreBar = ({ category, score }) => {
  const config = CATEGORIES[category] || CATEGORIES.technical;
  const Icon = config.icon;
  
  const getColor = (score) => {
    if (score >= 80) return "bg-green-500";
    if (score >= 60) return "bg-yellow-500";
    if (score >= 40) return "bg-orange-500";
    return "bg-red-500";
  };
  
  return (
    <div className="flex items-center gap-2">
      <Icon className={cn("w-4 h-4 flex-shrink-0", config.color)} />
      <span className="text-xs text-zinc-400 font-mono w-24 truncate">{config.label}</span>
      <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
        <div 
          className={cn("h-full transition-all duration-500 rounded-full", getColor(score))}
          style={{ width: `${score}%` }}
        />
      </div>
      <span className={cn("text-xs font-mono w-8 text-right", 
        score >= 80 ? "text-green-500" : score >= 60 ? "text-yellow-500" : score >= 40 ? "text-orange-500" : "text-red-500"
      )}>{score}</span>
    </div>
  );
};

// Issue Card Component
const IssueCard = ({ issue, isExpanded, onToggle }) => {
  const getSeverityStyles = (severity) => {
    switch (severity) {
      case "critical":
        return { 
          bg: "bg-red-500/10", 
          border: "border-red-500/30", 
          icon: XCircle, 
          iconColor: "text-red-500",
          badge: "bg-red-500/20 text-red-400"
        };
      case "warning":
        return { 
          bg: "bg-orange-500/10", 
          border: "border-orange-500/30", 
          icon: AlertTriangle, 
          iconColor: "text-orange-500",
          badge: "bg-orange-500/20 text-orange-400"
        };
      default:
        return { 
          bg: "bg-blue-500/10", 
          border: "border-blue-500/30", 
          icon: Info, 
          iconColor: "text-blue-500",
          badge: "bg-blue-500/20 text-blue-400"
        };
    }
  };
  
  const styles = getSeverityStyles(issue.severity);
  const IconComponent = styles.icon;
  const categoryConfig = CATEGORIES[issue.category] || CATEGORIES.technical;
  const CategoryIcon = categoryConfig.icon;
  
  return (
    <div className={cn("rounded-sm border overflow-hidden", styles.bg, styles.border)}>
      <div 
        className="p-3 cursor-pointer hover:bg-white/5 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-start gap-3">
          <IconComponent className={cn("w-5 h-5 flex-shrink-0 mt-0.5", styles.iconColor)} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h4 className="text-sm text-white font-mono">{issue.title}</h4>
              <span className={cn("text-[10px] px-1.5 py-0.5 rounded font-mono uppercase", styles.badge)}>
                {issue.severity}
              </span>
              <span className={cn("text-[10px] px-1.5 py-0.5 rounded font-mono flex items-center gap-1", 
                "bg-zinc-700/50 text-zinc-400")}>
                <CategoryIcon className="w-3 h-3" />
                {categoryConfig.label}
              </span>
              {issue.affected_count > 0 && (
                <span className="text-[10px] text-zinc-500 font-mono">
                  {issue.affected_count} affected
                </span>
              )}
            </div>
            <p className="text-xs text-zinc-400">{issue.description}</p>
          </div>
          <button className="text-zinc-500 p-1">
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>
      
      {isExpanded && (
        <div className="px-3 pb-3 border-t border-zinc-700/50">
          {issue.recommendation && (
            <div className="mt-2 p-2 bg-zinc-800/50 rounded-sm">
              <p className="text-xs text-zinc-300">
                <span className="text-green-500 font-mono">💡 Fix:</span> {issue.recommendation}
              </p>
            </div>
          )}
          {issue.affected_urls && issue.affected_urls.length > 0 && (
            <div className="mt-2">
              <p className="text-xs text-zinc-500 font-mono mb-1">
                Affected URLs ({issue.affected_urls.length} total):
              </p>
              <div className="max-h-64 overflow-y-auto space-y-1 bg-zinc-900/50 p-2 rounded">
                {issue.affected_urls.map((urlData, idx) => {
                  // Handle both string URLs and object URLs (for internal link issues)
                  const isObject = typeof urlData === 'object' && urlData !== null;
                  const pageUrl = isObject ? (urlData.page || urlData.url || '') : urlData;
                  const linkUrl = isObject ? urlData.link : null;
                  const displayUrl = pageUrl || (isObject ? JSON.stringify(urlData) : urlData);
                  
                  return (
                    <div key={idx} className="text-xs text-zinc-400 font-mono py-0.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-zinc-600 flex-shrink-0">{idx + 1}.</span>
                        <a href={pageUrl} target="_blank" rel="noopener noreferrer" 
                           className="hover:text-white break-all min-w-0" title={pageUrl}>
                          {displayUrl}
                        </a>
                        <a href={pageUrl} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                          <ExternalLink className="w-3 h-3 opacity-50 hover:opacity-100" />
                        </a>
                      </div>
                      {linkUrl && (
                        <div className="ml-5 mt-0.5 text-zinc-500 flex items-center gap-1">
                          <span className="text-zinc-600">↳</span>
                          <span className="break-all" title={linkUrl}>Link: {linkUrl}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Site Card Component
const SiteCard = ({ site, onScan, onDelete, onSelect, isSelected, onImportUrls }) => {
  const getStatusIcon = () => {
    switch (site.status) {
      case "COMPLETED":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "SCANNING":
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      case "ERROR":
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-zinc-500" />;
    }
  };
  
  const getHealthColor = (score) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-yellow-500";
    if (score >= 40) return "text-orange-500";
    return "text-red-500";
  };

  return (
    <div 
      onClick={() => onSelect(site)}
      className={cn(
        "bg-zinc-900 border rounded-sm p-4 cursor-pointer transition-all hover:border-zinc-600",
        isSelected ? "border-green-500/50 bg-zinc-900/80" : "border-zinc-800"
      )}
      data-testid={`techseo-site-card-${site.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <span className="text-white font-mono text-sm truncate max-w-[180px]" title={site.name}>
            {site.name}
          </span>
        </div>
      </div>
      
      <p className="text-[10px] text-zinc-600 font-mono mb-3 truncate" title={site.domain}>
        {site.domain}
      </p>
      
      {/* Health Score */}
      {site.health_score != null && site.status !== "SCANNING" && (
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Shield className={cn("w-5 h-5", getHealthColor(site.health_score))} />
            <span className={cn("text-2xl font-bold font-mono", getHealthColor(site.health_score))}>
              {site.health_score}
            </span>
            <span className="text-xs text-zinc-500 font-mono">/100</span>
          </div>
        </div>
      )}

      {/* Issue counts */}
      {site.status !== "SCANNING" && (site.critical_issues > 0 || site.warning_issues > 0 || site.info_issues > 0 || site.health_score != null) && (
        <div className="flex gap-2 text-[10px] font-mono mb-2">
          {site.critical_issues > 0 && (
            <span className="text-red-500 flex items-center gap-1">
              <XCircle className="w-3 h-3" /> {site.critical_issues}
            </span>
          )}
          {site.warning_issues > 0 && (
            <span className="text-orange-500 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> {site.warning_issues}
            </span>
          )}
          {site.info_issues > 0 && (
            <span className="text-blue-500 flex items-center gap-1">
              <Info className="w-3 h-3" /> {site.info_issues}
            </span>
          )}
          {site.critical_issues === 0 && site.warning_issues === 0 && site.info_issues === 0 && (
            <span className="text-green-500 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> No issues
            </span>
          )}
        </div>
      )}

      {site.status === "SCANNING" && (
        <div className="mb-3">
          <div className="flex justify-between text-[10px] text-zinc-500 font-mono mb-1">
            <span>Analyzing...</span>
            <span>{site.scanned_pages || 0} / {site.total_pages || '?'} pages</span>
          </div>
          <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-500 transition-all duration-300"
              style={{ width: `${site.total_pages ? (site.scanned_pages / site.total_pages * 100) : 0}%` }}
            />
          </div>
        </div>
      )}

      {site.status === "ERROR" && (
        <div className="mb-3">
          <p className="text-[10px] text-red-400/80 font-mono mb-1.5 line-clamp-2">{site.error_message || "Scan failed"}</p>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onImportUrls && onImportUrls(site.id);
            }}
            className="w-full text-[10px] font-mono px-2 py-1.5 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border border-blue-500/30 rounded-sm transition-colors"
            data-testid={`import-urls-btn-${site.id}`}
          >
            Import URLs Manually
          </button>
        </div>
      )}

      {site.last_scan_at && (
        <p className="text-[10px] text-zinc-600 font-mono mb-2">
          Last: {new Date(site.last_scan_at).toLocaleString()}
        </p>
      )}

      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
        <SnapshotCompare 
          siteId={site.id} 
          siteName={site.name} 
          monitorType="techseo"
          buttonClassName="flex-1 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm font-mono text-[10px] h-7 border-zinc-700"
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onScan(site.id)}
          disabled={site.status === "SCANNING"}
          className="flex-1 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm font-mono text-[10px] h-7"
        >
          <RefreshCw className={cn("w-3 h-3 mr-1", site.status === "SCANNING" && "animate-spin")} />
          Rescan
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(site.id)}
          className="text-red-500 hover:text-red-400 hover:bg-red-950/30 rounded-sm font-mono text-[10px] h-7 px-2"
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
};

// Main TechSEO Monitor Component
export const TechSEOMonitor = () => {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteDetails, setSiteDetails] = useState(null);
  const [sitemapUrl, setSitemapUrl] = useState("");
  const [siteName, setSiteName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [expandedIssues, setExpandedIssues] = useState(new Set());
  
  // Manual URL import
  const [showImportModal, setShowImportModal] = useState(false);
  const [importSiteId, setImportSiteId] = useState(null);
  const [importUrls, setImportUrls] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  
  // Groups
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [filterGroup, setFilterGroup] = useState("all");

  // Fetch groups
  const fetchGroups = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/groups`);
      setGroups(response.data);
    } catch (e) {
      console.error("Failed to fetch groups:", e);
    }
  }, []);

  const toggleGroupSelection = (groupId) => {
    setSelectedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const filteredSites = sites.filter((site) => {
    // Group filter
    if (filterGroup !== "all") {
      if (!site.groups || !site.groups.includes(filterGroup)) {
        return false;
      }
    }
    // Search filter
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      site.name?.toLowerCase().includes(query) ||
      site.domain?.toLowerCase().includes(query)
    );
  });

  const fetchSites = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/techseo-monitor`);
      setSites(response.data);
      
      if (response.data.length > 0 && !selectedSite) {
        setSelectedSite(response.data[0]);
      }
    } catch (e) {
      console.error("Failed to fetch sites:", e);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSite]);

  const fetchSiteDetails = useCallback(async () => {
    if (!selectedSite) return;
    
    setIsLoadingDetails(true);
    try {
      const response = await axios.get(`${API}/techseo-monitor/${selectedSite.id}`);
      setSiteDetails(response.data);
    } catch (e) {
      console.error("Failed to fetch site details:", e);
    } finally {
      setIsLoadingDetails(false);
    }
  }, [selectedSite]);

  useEffect(() => {
    fetchSites();
    fetchGroups();
    const interval = setInterval(fetchSites, 10000);
    return () => clearInterval(interval);
  }, [fetchSites, fetchGroups]);

  useEffect(() => {
    if (selectedSite) {
      fetchSiteDetails();
      setExpandedIssues(new Set());
    }
  }, [selectedSite, fetchSiteDetails]);

  const handleAddSite = async () => {
    if (!sitemapUrl) {
      toast.error("Please enter a sitemap URL");
      return;
    }

    setIsAdding(true);
    try {
      const response = await axios.post(`${API}/techseo-monitor`, {
        sitemap_url: sitemapUrl,
        name: siteName,
        groups: selectedGroups
      });
      toast.success("Site added - TechSEO analysis started");
      setSitemapUrl("");
      setSiteName("");
      setSelectedGroups([]);
      fetchSites();
      setSelectedSite(response.data);
    } catch (e) {
      const message = e.response?.data?.detail || "Failed to add site";
      toast.error(message);
    } finally {
      setIsAdding(false);
    }
  };

  const handleScan = async (siteId) => {
    try {
      await axios.post(`${API}/techseo-monitor/${siteId}/scan`);
      toast.success("TechSEO scan started");
      fetchSites();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start scan");
    }
  };

  const handleImportUrls = async () => {
    if (!importUrls.trim() || !importSiteId) return;
    
    const urls = importUrls.split("\n").map(u => u.trim()).filter(u => u.startsWith("http"));
    if (urls.length === 0) {
      toast.error("No valid URLs found. Each URL must start with http:// or https://");
      return;
    }
    
    setIsImporting(true);
    try {
      const response = await axios.post(`${API}/techseo-monitor/${importSiteId}/manual-urls`, { urls });
      toast.success(response.data.message);
      setShowImportModal(false);
      setImportUrls("");
      setImportSiteId(null);
      fetchSites();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to import URLs");
    } finally {
      setIsImporting(false);
    }
  };

  const handleDelete = async (siteId) => {
    if (!window.confirm("Delete this site and all its data?")) return;
    
    try {
      await axios.delete(`${API}/techseo-monitor/${siteId}`);
      toast.success("Site deleted");
      if (selectedSite?.id === siteId) {
        setSelectedSite(null);
        setSiteDetails(null);
      }
      fetchSites();
    } catch (e) {
      toast.error("Failed to delete site");
    }
  };

  const handleExportCSV = async (siteId) => {
    try {
      const response = await axios.get(`${API}/techseo-monitor/${siteId}/export-csv`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `techseo_issues_${selectedSite?.domain || 'site'}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("CSV exported successfully");
    } catch (e) {
      toast.error("Failed to export CSV");
    }
  };

  const handleExportJSON = async (siteId) => {
    try {
      const response = await axios.get(`${API}/techseo-monitor/${siteId}/export-json`);
      
      // Create download link
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `techseo_issues_${selectedSite?.domain || 'site'}_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("JSON exported successfully");
    } catch (e) {
      toast.error("Failed to export JSON");
    }
  };

  const toggleIssue = (idx) => {
    const newExpanded = new Set(expandedIssues);
    if (newExpanded.has(idx)) {
      newExpanded.delete(idx);
    } else {
      newExpanded.add(idx);
    }
    setExpandedIssues(newExpanded);
  };

  // Filter issues
  const filteredIssues = (siteDetails?.issues || []).filter(issue => {
    if (categoryFilter !== "all" && issue.category !== categoryFilter) return false;
    if (severityFilter !== "all" && issue.severity !== severityFilter) return false;
    return true;
  });

  // Group issues by category for summary
  const issuesByCategory = (siteDetails?.issues || []).reduce((acc, issue) => {
    acc[issue.category] = (acc[issue.category] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-6" data-testid="techseo-monitor">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white font-mono flex items-center gap-2">
          <Shield className="w-6 h-6 text-green-500" />
          TechSEO Monitoring
        </h1>
        <p className="text-zinc-500 text-sm font-mono mt-1">
          Comprehensive site health: indexability, content, links, images, technical, mobile, security, performance
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Panel - Sites List */}
        <div className="lg:col-span-3">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
            <h2 className="text-sm font-mono text-white mb-4 flex items-center gap-2">
              <Globe className="w-4 h-4 text-green-500" />
              MONITORED SITES
            </h2>

            {/* Add Site Form */}
            <div className="space-y-2 mb-4">
              <Input
                placeholder="Site name (optional)"
                value={siteName}
                onChange={(e) => setSiteName(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white font-mono text-xs h-8"
                data-testid="techseo-site-name-input"
              />
              <div className="flex gap-2">
                <Input
                  placeholder="https://example.com/sitemap.xml"
                  value={sitemapUrl}
                  onChange={(e) => setSitemapUrl(e.target.value)}
                  className="flex-1 bg-zinc-800 border-zinc-700 text-white font-mono text-xs h-8"
                  data-testid="techseo-sitemap-input"
                />
                <Button
                  onClick={handleAddSite}
                  disabled={isAdding || !sitemapUrl}
                  className="bg-green-600 hover:bg-green-700 text-white font-mono text-xs h-8 px-3"
                  data-testid="techseo-add-btn"
                >
                  {isAdding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                </Button>
              </div>
              
              {/* Group Selection */}
              {groups.length > 0 && (
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="text-[10px] text-zinc-500 font-mono">Groups:</span>
                  {groups.map((group) => (
                    <button
                      key={group.id}
                      type="button"
                      onClick={() => toggleGroupSelection(group.id)}
                      className={cn(
                        "flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono transition-colors",
                        selectedGroups.includes(group.id)
                          ? "bg-zinc-700 text-white"
                          : "bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800"
                      )}
                    >
                      <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: group.color }} />
                      {group.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Search and Group Filter */}
            <div className="flex gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  placeholder="Search sites..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 bg-zinc-800 border-zinc-700 text-white font-mono text-xs h-8"
                  data-testid="techseo-search-input"
                />
              </div>
              {groups.length > 0 && (
                <select
                  value={filterGroup}
                  onChange={(e) => setFilterGroup(e.target.value)}
                  className="bg-zinc-800 border border-zinc-700 rounded-sm px-1 py-1 text-[10px] text-zinc-300 font-mono h-8"
                  data-testid="techseo-group-filter"
                >
                  <option value="all">All</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Sites List */}
            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-zinc-800 border border-zinc-700 rounded-sm p-4 animate-pulse">
                    <div className="h-4 bg-zinc-700 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-zinc-700 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : filteredSites.length === 0 ? (
              <div className="text-center py-8 text-zinc-500 font-mono text-sm">
                <Shield className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No sites yet</p>
                <p className="text-xs mt-1">Add a URL to start</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[calc(100vh-420px)] overflow-y-auto pr-1">
                {filteredSites.map((site) => (
                  <SiteCard
                    key={site.id}
                    site={site}
                    onScan={handleScan}
                    onDelete={handleDelete}
                    onSelect={setSelectedSite}
                    isSelected={selectedSite?.id === site.id}
                    onImportUrls={(siteId) => {
                      setImportSiteId(siteId);
                      setImportUrls("");
                      setShowImportModal(true);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Site Details */}
        <div className="lg:col-span-9">
          {selectedSite && siteDetails ? (
            <div className="space-y-4">
              {/* Top Row - Health Score & Category Scores */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Health Score Card */}
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-mono text-zinc-400">HEALTH SCORE</h3>
                    <div className="flex items-center gap-2">
                      {siteDetails.sitemap_types && siteDetails.sitemap_types.length > 0 && (
                        <span className="text-[10px] text-zinc-600 font-mono">
                          Types: {siteDetails.sitemap_types.join(', ')}
                        </span>
                      )}
                      <a href={siteDetails.url} target="_blank" rel="noopener noreferrer"
                         className="text-xs text-zinc-500 hover:text-white font-mono flex items-center gap-1">
                        {siteDetails.domain}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <HealthMeter score={siteDetails.health_score} size={120} />
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                    <div>
                      <div className="text-xl font-bold text-red-500 font-mono">{siteDetails.critical_issues}</div>
                      <div className="text-[10px] text-zinc-500">Critical</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-orange-500 font-mono">{siteDetails.warning_issues}</div>
                      <div className="text-[10px] text-zinc-500">Warnings</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-blue-500 font-mono">{siteDetails.info_issues}</div>
                      <div className="text-[10px] text-zinc-500">Info</div>
                    </div>
                  </div>
                </div>

                {/* Category Scores */}
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4 md:col-span-2">
                  <h3 className="text-sm font-mono text-zinc-400 mb-4">CATEGORY SCORES</h3>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                    <CategoryScoreBar category="indexability" score={siteDetails.indexability_score || 0} />
                    <CategoryScoreBar category="content" score={siteDetails.content_score || 0} />
                    <CategoryScoreBar category="links" score={siteDetails.links_score || 0} />
                    <CategoryScoreBar category="images" score={siteDetails.images_score || 0} />
                    <CategoryScoreBar category="performance" score={siteDetails.performance_score || 0} />
                    <CategoryScoreBar category="mobile" score={siteDetails.mobile_score || 0} />
                    <CategoryScoreBar category="security" score={siteDetails.security_score || 0} />
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                {[
                  { label: "Pages", value: siteDetails.pages_crawled, icon: FileText },
                  { label: "Internal Links", value: siteDetails.internal_links_found, icon: Link2 },
                  { label: "External Links", value: siteDetails.external_links_found, icon: ExternalLink },
                  { label: "Broken Links", value: siteDetails.broken_links, icon: AlertTriangle, danger: siteDetails.broken_links > 0 },
                  { label: "Images", value: siteDetails.images_found || 0, icon: ImageIcon },
                  { label: "Avg Load", value: `${siteDetails.avg_load_time}s`, icon: Zap, danger: siteDetails.avg_load_time > 3 },
                ].map((stat, idx) => (
                  <div key={idx} className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-3 text-center">
                    <stat.icon className={cn("w-4 h-4 mx-auto mb-1", stat.danger ? "text-red-500" : "text-zinc-500")} />
                    <div className={cn("text-lg font-bold font-mono", stat.danger ? "text-red-500" : "text-white")}>
                      {stat.value}
                    </div>
                    <div className="text-[10px] text-zinc-500 font-mono">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Issues Section */}
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                  <h3 className="text-sm font-mono text-white flex items-center gap-2 shrink-0">
                    <AlertCircle className="w-4 h-4 text-zinc-500" />
                    ISSUES & RECOMMENDATIONS ({filteredIssues.length})
                  </h3>
                  
                  {/* Filters and Export */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Export Buttons */}
                    {siteDetails?.issues?.length > 0 && (
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleExportCSV(selectedSite.id)}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white text-xs font-mono rounded-sm transition-colors"
                          title="Export as CSV"
                          data-testid="export-csv-btn"
                        >
                          <Download className="w-3 h-3" />
                          CSV
                        </button>
                        <button
                          onClick={() => handleExportJSON(selectedSite.id)}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white text-xs font-mono rounded-sm transition-colors"
                          title="Export as JSON"
                          data-testid="export-json-btn"
                        >
                          <Download className="w-3 h-3" />
                          JSON
                        </button>
                      </div>
                    )}
                    
                    {/* Category Filter */}
                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      className="bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs font-mono rounded-sm px-2 py-1.5"
                    >
                      <option value="all">All Categories</option>
                      {Object.entries(CATEGORIES).map(([key, val]) => (
                        <option key={key} value={key}>
                          {val.label} {issuesByCategory[key] ? `(${issuesByCategory[key]})` : ''}
                        </option>
                      ))}
                    </select>
                    
                    {/* Severity Filter */}
                    <div className="flex gap-1">
                      {["all", "critical", "warning", "info"].map((sev) => (
                        <button
                          key={sev}
                          onClick={() => setSeverityFilter(sev)}
                          className={cn(
                            "px-2.5 py-1.5 font-mono text-xs rounded-sm transition-colors capitalize",
                            severityFilter === sev 
                              ? sev === "critical" ? "bg-red-500/20 text-red-400"
                                : sev === "warning" ? "bg-orange-500/20 text-orange-400"
                                : sev === "info" ? "bg-blue-500/20 text-blue-400"
                                : "bg-zinc-700 text-white"
                              : "text-zinc-500 hover:bg-zinc-800 hover:text-white"
                          )}
                        >
                          {sev}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2 max-h-[calc(100vh-550px)] overflow-y-auto">
                  {filteredIssues.length === 0 ? (
                    <div className="text-center py-8 text-zinc-500 font-mono text-sm">
                      {categoryFilter === "all" && severityFilter === "all" ? (
                        <>
                          <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                          <p>No issues found!</p>
                          <p className="text-xs mt-1">Your site looks healthy</p>
                        </>
                      ) : (
                        <p>No matching issues</p>
                      )}
                    </div>
                  ) : (
                    filteredIssues.map((issue, idx) => (
                      <IssueCard 
                        key={idx} 
                        issue={issue} 
                        isExpanded={expandedIssues.has(idx)}
                        onToggle={() => toggleIssue(idx)}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
          ) : isLoadingDetails ? (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-8 flex items-center justify-center h-[calc(100vh-200px)]">
              <div className="text-center">
                <RefreshCw className="w-8 h-8 text-zinc-500 animate-spin mx-auto mb-2" />
                <p className="text-zinc-500 font-mono text-sm">Loading site details...</p>
              </div>
            </div>
          ) : (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-8 flex items-center justify-center h-[calc(100vh-200px)]">
              <div className="text-center text-zinc-500 font-mono">
                <Shield className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p className="text-sm">Select a site to view TechSEO details</p>
                <p className="text-xs mt-1">or add a new URL</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Import URLs Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowImportModal(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-mono text-sm mb-1">Import URLs Manually</h3>
            <p className="text-zinc-500 text-xs font-mono mb-4">
              Paste one URL per line. Use this when the sitemap can't be fetched automatically (e.g., bot protection).
            </p>
            <textarea
              value={importUrls}
              onChange={(e) => setImportUrls(e.target.value)}
              placeholder={"https://example.com/\nhttps://example.com/about\nhttps://example.com/services"}
              className="w-full h-48 bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs font-mono rounded-sm p-3 mb-3 resize-none focus:outline-none focus:border-zinc-500"
              data-testid="import-urls-textarea"
            />
            <p className="text-zinc-600 text-[10px] font-mono mb-4">
              {importUrls.split("\n").filter(u => u.trim().startsWith("http")).length} valid URLs detected
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowImportModal(false)}
                className="text-zinc-400 hover:text-white font-mono text-xs"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleImportUrls}
                disabled={isImporting || !importUrls.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white font-mono text-xs"
                data-testid="confirm-import-urls-btn"
              >
                {isImporting ? "Importing..." : "Import & Scan"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TechSEOMonitor;
